import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/Button';
import { useLanguage } from './LanguageContext';
import { ArrowUpRight, Calendar, PieChart } from 'lucide-react';
interface SlideTheRaiseProps {
  onContinue: () => void;
}
export function SlideTheRaise({
  onContinue
}: SlideTheRaiseProps) {
  const {
    t
  } = useLanguage();
  const [stage, setStage] = useState(0);
  useEffect(() => {
    const t1 = setTimeout(() => setStage(1), 250);
    const t2 = setTimeout(() => setStage(2), 700);
    const t3 = setTimeout(() => setStage(3), 1200);
    const t4 = setTimeout(() => setStage(4), 1700);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      clearTimeout(t4);
    };
  }, []);
  const allocations = [{
    label: t('Account Management', 'Account Management'),
    pct: 60,
    color: '#00D4AA',
    bg: 'bg-teal-400/10',
    border: 'border-teal-400/40',
    text: 'text-teal-400'
  }, {
    label: t('AI Optimization', 'AI-optimering'),
    pct: 20,
    color: '#7DD3FC',
    bg: 'bg-sky-400/10',
    border: 'border-sky-400/40',
    text: 'text-sky-400'
  }, {
    label: t('Delivery Capability', 'Leveringskapacitet'),
    pct: 20,
    color: '#A5B4FC',
    bg: 'bg-indigo-400/10',
    border: 'border-indigo-400/40',
    text: 'text-indigo-400'
  }];
  const milestones = [{
    quarter: 'Q2 2026',
    title: 'Pipeline Conversion',
    color: '#00D4AA',
    desc: 'Convert Stage I & II pipeline into signed contracts.'
  }, {
    quarter: 'Q3 2026',
    title: 'ARR Milestones',
    color: '#7DD3FC',
    desc: 'Hit recurring revenue targets across enterprise accounts.'
  }, {
    quarter: 'Q4 2026',
    title: 'Enterprise Expansion',
    color: '#A5B4FC',
    desc: 'Scale to Stage III & IV global pharma deployments.'
  }];
  // Donut chart geometry
  const size = 280;
  const cx = size / 2;
  const cy = size / 2;
  const r = 100;
  const stroke = 36;
  // Calculate stroke-dasharray segments for each slice
  const circumference = 2 * Math.PI * r;
  let cumulative = 0;
  const segments = allocations.map(a => {
    const length = a.pct / 100 * circumference;
    const offset = cumulative;
    cumulative += length;
    return {
      ...a,
      length,
      offset
    };
  });
  return <div className="h-full w-full flex flex-col px-8 py-10 md:px-20 md:py-12 max-w-7xl mx-auto" data-id="element-843">
      {/* Heading */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: stage >= 1 ? 1 : 0,
      y: stage >= 1 ? 0 : 20
    }} data-id="element-844">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="text-center space-y-4" data-id="element-845">
          <p className="text-teal-400 font-semibold tracking-widest uppercase text-sm" data-id="element-846">
            {t('The Ask', 'Investeringen')}
          </p>
          <h2 className="text-4xl md:text-5xl font-serif text-white" data-id="element-847">
            {t('The Raise', 'Kapitalrejsningen')}
          </h2>
        </motion.div>
        <p className="text-teal-400 text-base md:text-lg font-medium mt-1" data-id="element-848">
          Raising DKK 3–4M
        </p>
      </motion.div>

      <div className="flex-1 grid grid-cols-1 lg:grid-cols-2 gap-10 mt-8 items-center" data-id="element-849">
        {/* LEFT — donut + allocations */}
        <div className="flex flex-col items-center gap-6" data-id="element-850">
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: stage >= 2 ? 1 : 0,
          scale: stage >= 2 ? 1 : 0.9
        }} transition={{
          duration: 0.6
        }} className="relative" data-id="element-851">
            <svg width={size} height={size} className="-rotate-90" data-id="element-852">
              {/* Track */}
              <circle cx={cx} cy={cy} r={r} fill="none" stroke="#1a2542" strokeWidth={stroke} data-id="element-853" />
              {/* Segments */}
              {segments.map((s, i) => <motion.circle key={s.label} cx={cx} cy={cy} r={r} fill="none" stroke={s.color} strokeWidth={stroke} strokeDasharray={`${s.length} ${circumference}`} strokeDashoffset={-s.offset} initial={{
              pathLength: 0,
              opacity: 0
            }} animate={{
              pathLength: stage >= 2 ? 1 : 0,
              opacity: stage >= 2 ? 1 : 0
            }} transition={{
              duration: 0.8,
              delay: 0.2 + i * 0.2,
              ease: 'easeOut'
            }} data-id="element-854" />)}
            </svg>
            {/* Center label */}
            <div className="absolute inset-0 flex flex-col items-center justify-center" data-id="element-855">
              <motion.span initial={{
              opacity: 0,
              scale: 0.8
            }} animate={{
              opacity: stage >= 2 ? 1 : 0,
              scale: stage >= 2 ? 1 : 0.8
            }} transition={{
              delay: 0.6
            }} className="text-4xl md:text-5xl font-bold text-white tracking-tight" data-id="element-856">
                DKK 3–4M
              </motion.span>
              <motion.span initial={{
              opacity: 0
            }} animate={{
              opacity: stage >= 2 ? 1 : 0
            }} transition={{
              delay: 0.9
            }} className="text-white/55 text-sm mt-1" data-id="element-857">
                Raise
              </motion.span>
            </div>
          </motion.div>

          {/* Allocation bars */}
          <div className="w-full space-y-2.5 mt-2" data-id="element-858">
            {allocations.map((a, i) => <motion.div key={a.label} initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: stage >= 3 ? 1 : 0,
            x: stage >= 3 ? 0 : -20
          }} transition={{
            delay: i * 0.1,
            duration: 0.4
          }} className={`flex items-center justify-between bg-navy-900/60 border border-navy-700 rounded-xl px-4 py-2.5`} data-id="element-859">
                <div className="flex items-center gap-3" data-id="element-860">
                  <span className="w-2.5 h-2.5 rounded-full" style={{
                backgroundColor: a.color
              }} data-id="element-861" />
                  <span className="text-white/85 text-sm font-semibold" data-id="element-862">
                    {a.label}
                  </span>
                </div>
                <span className="text-base font-bold tabular-nums" style={{
              color: a.color
            }} data-id="element-863">
                  {a.pct}%
                </span>
              </motion.div>)}
          </div>
        </div>

        {/* RIGHT — outcome + milestones */}
        <div className="flex flex-col gap-5" data-id="element-864">
          {/* Outcome card */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: stage >= 2 ? 1 : 0,
          y: stage >= 2 ? 0 : 20
        }} transition={{
          duration: 0.5,
          delay: 0.3
        }} className="bg-teal-400/5 border border-teal-400/30 rounded-2xl p-5" data-id="element-865">
            <div className="flex items-center gap-2 mb-4" data-id="element-866">
              <PieChart className="w-5 h-5 text-white/45" data-id="element-867" />
              <h3 className="text-sm font-bold text-white/45 uppercase tracking-widest" data-id="element-868">
                {t('Allocation', 'Allokering')}
              </h3>
            </div>
            <div className="flex items-center gap-2 mb-2" data-id="element-869">
              <ArrowUpRight className="w-5 h-5 text-teal-400" data-id="element-870" />
              <h3 className="text-xl font-bold text-white" data-id="element-871">
                {t('The Outcome', 'Resultatet')}
              </h3>
            </div>
            <p className="text-white/70 leading-relaxed" data-id="element-872">
              {t('A scalable AI-native agency with', 'Et skalerbart AI-native bureau med')}{' '}
              <span className="text-teal-400 font-semibold" data-id="element-873">
                {t('compounding learning dynamics', 'akkumulerende læringsdynamikker')}
              </span>{' '}
              {t('and', 'og')}{' '}
              <span className="text-teal-400 font-semibold" data-id="element-874">
                {t('recurring runtime revenue', 'tilbagevendende driftsomsætning')}
              </span>
              .
            </p>
          </motion.div>
        </div>
      </div>

      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: stage >= 4 ? 1 : 0
    }} className="mt-6 flex justify-end" data-id="element-875">
        <Button onClick={onContinue} data-id="element-876">{t('Closing', 'Afslutning')}</Button>
      </motion.div>
    </div>;
}